import type { LucideIcon } from 'lucide-react'
import { Inbox, Search, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export type EmptyStateVariant = 'no-data' | 'no-match' | 'error'

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: EmptyStateVariant
  /** Override the default icon for this variant. */
  icon?: LucideIcon
  title: string
  description?: React.ReactNode
  /** Primary CTA — typically a <Button>. */
  action?: React.ReactNode
}

const defaultIcons: Record<EmptyStateVariant, LucideIcon> = {
  'no-data': Inbox,
  'no-match': Search,
  error: AlertTriangle,
}

/**
 * Spec §7.4: three standardized states.
 * - no-data: "No X yet — …"
 * - no-match: "No matches — clear filters"
 * - error: "Something went wrong — retry"
 *
 * Never renders the banned "Click to begin / Run Report" pattern
 * (Principle 1.3).
 */
export function EmptyState({
  variant = 'no-data',
  icon,
  title,
  description,
  action,
  className,
  ...rest
}: EmptyStateProps) {
  const Icon = icon ?? defaultIcons[variant]
  return (
    <div
      role={variant === 'error' ? 'alert' : undefined}
      data-variant={variant}
      className={cn(
        'flex flex-col items-center justify-center rounded-md border border-border-subtle bg-surface px-6 py-12 text-center text-secondary',
        className,
      )}
      {...rest}
    >
      <Icon aria-hidden="true" size={36} className="mb-3 text-tertiary opacity-60" />
      <h3 className="mb-1 text-lg font-semibold text-primary">{title}</h3>
      {description ? (
        <p className="mb-4 max-w-80 text-sm leading-normal text-secondary">{description}</p>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}
